import { useState } from "react";

import AttributesConfig from "../../../../shared/schema/comparisontool/Attributes";
import AudiencesConfig from "../../../../shared/schema/comparisontool/Audiences";
import BrandConfig from "../../../../shared/schema/comparisontool/Brand";
import ComparisonPageConfig from "../../../../shared/schema/comparisontool/ComparisonPage";
import ComparisonTypeConfig from "../../../../shared/schema/comparisontool/ComparisonType";
import ContactConfig from "../../../../shared/schema/comparisontool/Contact";
import CurrencyConfig from "../../../../shared/schema/comparisontool/Currency";
import DisclaimerConfig from "../../../../shared/schema/comparisontool/Disclaimer";
import FamilyConfig from "../../../../shared/schema/comparisontool/Family";
import FindByFeaturesPageConfig from "../../../../shared/schema/comparisontool/FindByFeaturesPage";
import GlobalSettingsConfig from "../../../../shared/schema/comparisontool/GlobalSettings";

import IndexPageConfig from "../../../../shared/schema/comparisontool/IndexPage";
import LocaleConfig from "../../../../shared/schema/comparisontool/Locale";
import PriceTypeConfig from "../../../../shared/schema/comparisontool/PriceType";
import ProductConfig from "../../../../shared/schema/comparisontool/Product";
import ProductTypeConfig from "../../../../shared/schema/comparisontool/ProductType";
import PromoConfig from "../../../../shared/schema/comparisontool/Promo";
import SpecificationStyleConfig from "../../../../shared/schema/comparisontool/SpecificationStyle";
import TCOConfig from "../../../../shared/schema/comparisontool/TCO";
import WinLogicConfig from "../../../../shared/schema/comparisontool/WinLogic";
import LogicBuilderConfig from "./collections/LogicBuilder";
import QuickCompareLandingConfig from "./collections/QuickCompareLanding";
import QuickComparePageConfig from "./collections/QuickComparePage";
import GlossaryTermConfig from "./collections/GlossaryTerm";
import { get_db_base, request_items } from "../../../../helpers/Functions";

function map_collection_items_by_id(collection, items) {
  let obj = {};
  obj.map = {};
  items.forEach((item) => (obj.map[item.id] = item));
  return obj;
}

const collateProjectData = (project, project_data) => {
  // Extract arrays and maps to array elements
  let collections = {
    _global: { project }
  };
  let collections_unmapped = {};
  let collections_maps = {};
  let collection_names = [];
  project_data.forEach((collection_info) => {
    collection_names.push(collection_info.name);
    collections[collection_info.name] = collection_info.data;
    collections_unmapped[collection_info.name] = collection_info.unmapped;
    collections_maps[collection_info.name] = collection_info.map;
  });

  // Perform mappings
  collection_names.forEach((collection_name) => {
    let next_collection = collections[collection_name];

    if (Array.isArray(next_collection))
      next_collection.forEach((item) =>
        item.map?.(collections, collections_maps)
      );
    else next_collection.map(collections, collections_maps);
  });

  const QCL_SPE_PROJECTS = new Set([
    "scanners"
    // ,"latin-scanners"
  ]);

  return {
    collections,
    collections_maps,
    collection_names,
    collections_unmapped,
    config: {
      current_project: project,
      available_features: {
        find_model_by_features_page: true,
        quick_compare_page: true,

        // Whether quick compare landing can be exported as comparison page PDF
        quick_compare_landing_spe: QCL_SPE_PROJECTS.has(project),

        // Disable buttons on Quick Compare page, and disable Landing page
        quick_compare_pdp: true,

        glossary_terms_page: false,

        // Remove the "Export" link & icon, along with the divider between it and "Top"
        single_page_export: true,

        // Remove completely when disabled (use "none" for first contact)
        sales_contacts: true,

        // First check if enabled, then for the access code
        preview_access_code: true,

        // Page types should be independently configurable
        // When disabled, do not render template. Also avoid loading invalid pages (
        // request by template type, for enabled types)
        comparison_types: {
          // Primary vs. Primary Template
          primary_vs_primaries: true,

          // Primary vs. Competitors
          primary_vs_competitors: true
        }

        /* The following items are "built in"
        'alc', // Audienced, Localized Content

        // Ideally there will be a setting for clearing old assets
        'gac', // (Generated) Asset Caching
        */
      }
    }
  };
};

const preview_collections_config = [
  GlobalSettingsConfig,
  BrandConfig,
  IndexPageConfig,
  PromoConfig,
  SpecificationStyleConfig,
  AttributesConfig,
  FindByFeaturesPageConfig,
  PriceTypeConfig,
  TCOConfig,
  ContactConfig,
  CurrencyConfig,
  LocaleConfig,
  WinLogicConfig,
  ComparisonTypeConfig,
  AudiencesConfig,
  QuickComparePageConfig,
  GlossaryTermConfig,
  LogicBuilderConfig,
  ProductConfig,
  ProductTypeConfig,
  FamilyConfig,
  DisclaimerConfig,
  ComparisonPageConfig
];

const landing_collections_config = [
  QuickCompareLandingConfig,
  ComparisonTypeConfig,
  SpecificationStyleConfig,
  AudiencesConfig,
  GlobalSettingsConfig,
  DisclaimerConfig,
  AttributesConfig,
  PriceTypeConfig,
  ProductTypeConfig,
  LocaleConfig,
  CurrencyConfig,
  WinLogicConfig,
  FamilyConfig,
  BrandConfig,
  LogicBuilderConfig
];

const useProjectData = (project) => {
  LocaleConfig.collection = "locales";
  const [projectData, setPd] = useState(null);
  //eslint-disable-next-line
  const [pdLoading, setPdLoading] = useState(false);
  //eslint-disable-next-line
  const [pdError, setPdError] = useState(false);
  const db_base = get_db_base(project);

  function request(
    collection,
    collection_name_override,
    is_single,
    fields_to_get,
    filter
  ) {
    return request_items(
      db_base,
      collection,
      collection_name_override,
      is_single,
      fields_to_get,
      filter
    );
  }

  function loadType(collection, fields, type, single, filter) {
    return new Promise((accept, reject) => {
      request(collection, null, single, fields, filter)
        .then((items) => {
          let obj = { name: collection };
          if (single) {
            obj.data = new type(items);
            obj.unmapped = items;
          } else {
            obj.data = items.map((item) => new type(item));
            obj.unmapped = items;
            let mapped_data = map_collection_items_by_id(collection, obj.data);

            obj = { ...obj, ...mapped_data };
          }

          accept(obj);
        })
        .catch((e) => reject(e));
    });
  }

  const load = (collection_configs) => {
    let promises = collection_configs.map(
      ({ collection, fields, adapter, single, filter }) =>
        loadType(collection, fields, adapter, single, filter)
    );

    return Promise.all(promises).then((project_data) => {
      const collated_data = collateProjectData(project, project_data);
      setPd(collated_data);
      return collated_data;
    });
  };

  const loadPreview = () => load(preview_collections_config);
  const loadLanding = (product_ids) => {
    const product_filter_addition = `&filter[id][_in]=${product_ids}`;
    const { filter, ...otherProps } = ProductConfig;
    const newProductConfig = {
      ...otherProps,
      filter: `${filter}${product_filter_addition}`
    };

    return load([...landing_collections_config, newProductConfig]);
  };

  const status = { projectData, pdLoading, pdError };
  const actions = { loadPreview, loadLanding };
  return [status, actions];
};

export default useProjectData;
